import React from 'react';
import { useHistory } from 'react-router-dom';
import { Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNumber } from 'lodash/fp';
import { routePaths } from '../../../../../routePaths';
import { OnSubmit } from '../types';
import ProductForm from '../ProductForm';
import { usePriceService } from '../../../../../utils/hooks/priceService';
import { Card, Typography } from '../../../../../ui-kit';
import { LocationReferrer } from '../../../../../types/locationReferrer';
import { useProductQueryContext } from '../../hooks';
import {
    ProductFormQueryComponent,
    refetchGetPaginatedProductsQuery,
    useCreateProductAndRecipeMutation,
} from '../../../../../generated/graphql';

const CreateProductForm = () => {
    const history = useHistory<LocationReferrer>();
    const { state = {} } = history.location;
    const { referrer } = state;
    const { priceService } = usePriceService();
    const { t } = useTranslation();
    const { query, categories: selectedCategories, page, rowsPerPage } = useProductQueryContext();
    const offset = page * rowsPerPage;

    const [createProductAndRecipe] = useCreateProductAndRecipeMutation({
        refetchQueries: [refetchGetPaginatedProductsQuery({
            pagination: { offset, limit: rowsPerPage },
            name: query,
            categories: selectedCategories.length > 0 ? selectedCategories : undefined,
        })]
    });

    const onSubmit: OnSubmit = async values => {
        const { priceSell: rawSell, printingDeviceIds, warehouseCardId, warehouseId, recipeLines, ...rest } = values;
        const priceSell = isNumber(rawSell) ? priceService.toInteger(rawSell) : undefined;

        const mappedRecipeLines = recipeLines?.map(({ absoluteWaste, wastePercentage, stockCard, ...other }) => ({
            ...other,
            waste: absoluteWaste,
        }));

        const recipe = isEmpty(mappedRecipeLines) ? undefined : {
            recipeLines: mappedRecipeLines,
        };

        try {
            await createProductAndRecipe({
                variables: {
                    product: {
                        priceSell: priceSell as number,
                        printingDeviceIds: printingDeviceIds.map(({ id }) => id),
                        stockCardId: warehouseCardId,
                        stockCardWarehouseId: warehouseId,
                        ...rest
                    },
                    recipe,
                },
            });
            history.push(referrer ||routePaths.products);
        } catch (error) {
            // nothing we can do
        }
    };

    return (
        <ProductFormQueryComponent nextFetchPolicy="cache-first">
            {({ data: { categories, taxes, devices, warehouses } }) => {
             return (
                 <Card>
                     <Box px={3} py={5}>
                         <Typography variant='h6' component='h1'>{t('Create Product')}</Typography>
                         <ProductForm
                             warehouses={warehouses}
                             submitText={t('Create')}
                             onSubmit={onSubmit}
                             devices={devices}
                             categories={categories}
                             taxes={taxes}
                         />
                     </Box>
                 </Card>
                );
            }}
        </ProductFormQueryComponent>
    );
};

export default CreateProductForm;
